import React, { useState } from 'react';
import { Input, Button, Select, DualDateRangePicker, Modal, Typography } from '@crisellevaldez/speculo-ui';
import { RotateCw, Search } from 'lucide-react';
import { FilterButton } from '../../common/FilterButton';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface CallStatsHeaderProps {
  onSearch: (query: string) => void;
  onFilterByAnsweredBy?: (answeredBy: string | null) => void;
  /** Multi-select by flow_id. Backend supports flow_ids on Calls. */
  onFilterByFlows?: (flowIds: string[]) => void;
  /** Multi-select by flow_type. Backend supports flow_types on Calls. */
  onFilterByFlowTypes?: (flowTypes: string[]) => void;
  onDateRangeChange: (range: DateRange) => void;
  onReset: () => void;
  searchQuery: string;
  selectedAnsweredBy?: string | null;
  selectedFlowIds?: string[];
  selectedFlowTypes?: string[];
  dateRange: DateRange;
  flowOptions: { value: string; label: string }[];
  /** Flow type options (e.g. Revive / Engage / Convert / Flywheel). */
  flowTypeOptions?: { value: string; label: string }[];
  isFlowOptionsLoading?: boolean;
  /** Hide the Flow Select on surfaces where it doesn't apply (e.g. Live Transfers). */
  hideFlowFilter?: boolean;
  /**
   * Hide the Answered By Select. Useful for surfaces where the filter
   * doesn't apply — e.g. Live Transfers (all rows are by definition
   * human-answered).
   */
  hideAnsweredBy?: boolean;
}

const ANSWERED_BY_OPTIONS = [
  { value: '', label: 'All Answered By' },
  { value: 'human', label: 'Human' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
];

export const CallStatsHeader: React.FC<CallStatsHeaderProps> = ({
  onSearch,
  onFilterByAnsweredBy,
  onFilterByFlows,
  onFilterByFlowTypes,
  onDateRangeChange,
  onReset,
  searchQuery,
  selectedAnsweredBy,
  selectedFlowIds = [],
  selectedFlowTypes = [],
  dateRange,
  flowOptions,
  flowTypeOptions = [],
  isFlowOptionsLoading,
  hideFlowFilter = false,
  hideAnsweredBy = false,
}) => {
  // Holds Flow / Flow Type / Date Range — Answered By stays inline as a quick filter.
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  // Modal-local staging: edits inside the modal don't fire onFilterBy* until
  // the user clicks OK. Initialized on every Open from the committed props.
  const [draftFlowIds, setDraftFlowIds] = useState<string[]>(selectedFlowIds);
  const [draftFlowTypes, setDraftFlowTypes] = useState<string[]>(selectedFlowTypes);
  const [draftDateRange, setDraftDateRange] = useState<DateRange>(dateRange);

  const openFilters = () => {
    // Snapshot the committed values into the draft so the modal opens with
    // current state. Avoids a useEffect sync.
    setDraftFlowIds(selectedFlowIds);
    setDraftFlowTypes(selectedFlowTypes);
    setDraftDateRange(dateRange);
    setIsFiltersOpen(true);
  };

  const applyFilters = () => {
    onFilterByFlows?.(draftFlowIds);
    onFilterByFlowTypes?.(draftFlowTypes);
    onDateRangeChange(draftDateRange);
    setIsFiltersOpen(false);
  };

  const closeFiltersWithoutApply = () => {
    // Discards draft changes — committed state stays untouched.
    setIsFiltersOpen(false);
  };

  // Only the in-modal filters contribute to the button badge; the inline
  // Answered By select shows its own state directly.
  const modalFilterCount =
    (!hideFlowFilter && selectedFlowIds.length > 0 ? 1 : 0) +
    (!hideFlowFilter && selectedFlowTypes.length > 0 ? 1 : 0) +
    (dateRange.from || dateRange.to ? 1 : 0);

  const hasActiveFilters =
    !!searchQuery ||
    modalFilterCount > 0 ||
    (!hideAnsweredBy && !!selectedAnsweredBy);

  const searchInput = (
    <Input
      type="text"
      placeholder="Search by name, phone..."
      value={searchQuery}
      onChange={(e) => onSearch(e.target.value)}
      startIcon={<Search className="h-4 w-4 text-muted-foreground" />}
    />
  );

  const answeredBySelect = (
    <Select
      options={ANSWERED_BY_OPTIONS}
      value={selectedAnsweredBy || ''}
      onChange={(value) => {
        const v = Array.isArray(value) ? value[0] : value;
        onFilterByAnsweredBy?.(v || null);
      }}
      placeholder="Answered By"
    />
  );

  // Modal-only controls — read/write the draft, not the committed props.
  const flowSelect = (
    <Select
      options={flowOptions}
      value={draftFlowIds}
      onChange={(value) => {
        const arr = Array.isArray(value) ? value : value ? [value] : [];
        setDraftFlowIds(arr.filter(Boolean) as string[]);
      }}
      placeholder="All Flows"
      multiple
      searchable
      isLoading={isFlowOptionsLoading}
    />
  );

  const flowTypeSelect = (
    <Select
      options={flowTypeOptions}
      value={draftFlowTypes}
      onChange={(value) => {
        const arr = Array.isArray(value) ? value : value ? [value] : [];
        setDraftFlowTypes(arr.filter(Boolean) as string[]);
      }}
      placeholder="All Flow Types"
      multiple
      searchable
      isLoading={isFlowOptionsLoading}
    />
  );

  const dateRangePicker = (
    <DualDateRangePicker
      value={draftDateRange}
      onChange={setDraftDateRange}
      showPresets
    />
  );

  // When there are no Flow / Flow Type filters to show, the modal would only
  // hold the Date Range — skip the modal and render the date picker inline.
  const inlineDateRangeOnly = hideFlowFilter;

  const inlineDateRangePicker = (
    <DualDateRangePicker value={dateRange} onChange={onDateRangeChange} showPresets />
  );

  return (
    <div className="w-full">
      <div>
        <div className="flex items-center gap-2 w-full flex-wrap">
          <div className="flex-1 min-w-[220px]">{searchInput}</div>
          {!hideAnsweredBy && (
            <div className="w-[160px] shrink-0">{answeredBySelect}</div>
          )}
          {inlineDateRangeOnly ? (
            <div className="shrink-0">{inlineDateRangePicker}</div>
          ) : (
            <FilterButton
              onClick={openFilters}
              activeCount={modalFilterCount}
              label="Filters"
              variant="outline"
              className="shrink-0 px-3"
              hideLabelOnMobile
            />
          )}
          {hasActiveFilters && (
            <Button
              variant="outline"
              className="p-2 justify-center shrink-0 ml-auto"
              onClick={onReset}
              title="Reset filters"
            >
              <RotateCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {!inlineDateRangeOnly && (
      <Modal isOpen={isFiltersOpen} onClose={closeFiltersWithoutApply} size="sm">
        <Modal.Header>
          <Typography variant="h3">Filters</Typography>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {!hideFlowFilter && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Flow
                </label>
                {flowSelect}
              </div>
            )}
            {!hideFlowFilter && flowTypeOptions.length > 0 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Flow Type
                </label>
                {flowTypeSelect}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Date Range
              </label>
              {dateRangePicker}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-center justify-between w-full gap-2">
            <Button
              variant="outline"
              onClick={() => {
                // Clear the draft AND fire the parent reset so the commit
                // happens in one step. Then close the modal.
                setDraftFlowIds([]);
                setDraftFlowTypes([]);
                setDraftDateRange({ from: null, to: null });
                onReset();
                setIsFiltersOpen(false);
              }}
              disabled={!hasActiveFilters}
              className="inline-flex items-center gap-1.5"
            >
              <RotateCw className="h-4 w-4" />
              Reset
            </Button>
            <Button variant="primary" onClick={applyFilters}>
              OK
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      )}
    </div>
  );
};
